import React, { useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { OrderList } from 'primereact/orderlist';
import { Checkbox } from 'primereact/checkbox';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../contexts/LanguageContext';
import useTablePreferences from '../../hooks/useTablePreferences';

// Column config objesinde DataTable'a gecmemesi gereken meta alanlar
const META_KEYS = ['lockedFromPrefs', 'lockedPosition', 'headerLabel', 'columnKey'];

const stripMeta = (col) => {
    const clean = {};
    Object.keys(col).forEach((k) => {
        if (!META_KEYS.includes(k)) clean[k] = col[k];
    });
    return clean;
};

const labelOf = (col) => col.headerLabel ?? (typeof col.header === 'string' ? col.header : col.field);

/**
 * PreferencesDataTable
 * DataTable sarmalayicisi: kullaniciya ozel, BE'de kalici kolon siralama + gizleme.
 *
 * @param {string} tableKey  Global benzersiz tablo anahtari
 * @param {Array}  columns   Kolon config dizisi (bkz. useTablePreferences)
 * @param {ReactNode} header Tablo basligina eklenecek opsiyonel icerik (butonun soluna)
 * @param {object} rest      Diger tum DataTable proplari
 */
const PreferencesDataTable = ({ tableKey, columns, header, children, ...rest }) => {
    const { th } = useLanguage();
    const { columns: renderColumns, allColumns, hiddenColumns, toggleHidden, reorder, reset } = useTablePreferences(tableKey, columns);

    const opRef = useRef(null);
    const toastRef = useRef(null);

    const showSaved = () => toastRef.current?.show({ severity: 'success', summary: th('tablePrefs.saved'), life: 1500 });

    const handleColReorder = (e) => {
        const newOrder = (e?.columns || []).map((c) => c?.props?.field).filter(Boolean);
        reorder(newOrder);
        showSaved();
    };

    const handleOrderListChange = (e) => {
        const newOrder = (e?.value || []).map((c) => c?.field).filter(Boolean);
        reorder(newOrder);
        showSaved();
    };

    const handleToggleVisibility = (field) => {
        toggleHidden(field);
        showSaved();
    };

    const handleReset = () => {
        reset();
        showSaved();
    };

    const orderItemTemplate = (col) => {
        const isVisible = !hiddenColumns.includes(col.field);
        const disableHide = isVisible && hiddenColumns.length >= allColumns.length - 1; // son gorunur kolon gizlenemez
        return (
            <div className="flex align-items-center gap-2 py-1">
                <Checkbox inputId={`prefcol-${col.field}`} checked={isVisible} disabled={disableHide} onChange={() => handleToggleVisibility(col.field)} />
                <i className="pi pi-bars text-500" />
                <label htmlFor={`prefcol-${col.field}`} className="cursor-pointer">
                    {labelOf(col)}
                </label>
            </div>
        );
    };

    const settingsButton = (
        <Button
            type="button"
            icon="pi pi-table"
            label={th('tablePrefs.title')}
            className="p-button-outlined p-button-sm p-overlay-badge"
            onClick={(e) => opRef.current?.toggle(e)}
            aria-label={th('tablePrefs.title')}
        >
            {hiddenColumns.length > 0 && <Badge value={hiddenColumns.length} severity="info" />}
        </Button>
    );

    const tableHeader = (
        <div className="flex align-items-center gap-2 flex-wrap">
            {settingsButton}
            {header && <div className="flex-1">{header}</div>}
        </div>
    );

    const mappedColumns = useMemo(
        () =>
            renderColumns.map((col) => {
                const clean = stripMeta(col);
                return <Column key={col.columnKey || col.field} reorderable={!col.lockedFromPrefs} {...clean} />;
            }),
        [renderColumns]
    );

    return (
        <>
            <Toast ref={toastRef} />

            <OverlayPanel ref={opRef} showCloseIcon dismissable style={{ width: '20rem' }}>
                <style>{`
                    .prefs-orderlist .p-orderlist-controls { display: none !important; }
                    .prefs-orderlist .p-orderlist-list-container { flex: 1 1 auto; width: 100%; }
                    .prefs-orderlist .p-orderlist { gap: 0; }
                `}</style>
                <div className="flex justify-content-between align-items-center mb-2">
                    <span className="font-semibold">{th('tablePrefs.title')}</span>
                    <Button type="button" label={th('tablePrefs.reset')} icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={handleReset} />
                </div>
                <small className="text-500 block mb-2">{th('tablePrefs.order')} / {th('tablePrefs.visibility')}</small>
                <OrderList
                    className="prefs-orderlist"
                    value={allColumns}
                    onChange={handleOrderListChange}
                    itemTemplate={orderItemTemplate}
                    dragdrop
                    dataKey="field"
                    listStyle={{ maxHeight: '22rem' }}
                />
            </OverlayPanel>

            <DataTable header={tableHeader} reorderableColumns onColReorder={handleColReorder} {...rest}>
                {mappedColumns}
            </DataTable>
        </>
    );
};

export default PreferencesDataTable;
